import {get as getCookie, set as setCookie} from 'es-cookie';
import {merge, objectToQueryString} from './utils';

const NONCE_ALPHABET = 'abcdefghijklmnopqrstuvwxyz0123456789';
const ACCESS_ERROR_CODES = {
    nonceInvalid: 'nonceInvalid',
    agegoError: 'agegoError',
    notVerified: 'notVerified',
    expired: 'expired',
}
const QUERY_PARAM_REGEXP = new RegExp(/[?&](ag_[a-z_]+)=([^&#]*)/g);

class AccessManager {
    constructor() {
        this.prefersLongSession = false;
    }

    configure(options) {
        this.options = options;
    }

    checkRedirectReturn(scope) {
        if (!scope.location.search || -1 === scope.location.search.indexOf('ag_verified')) {
            return false;
        }

        const queryParamMatches = scope.location.search.matchAll(QUERY_PARAM_REGEXP);
        const params = {};
        const nonce = this.options.nonce ? this.options.nonce : getCookie(this.options.cookies.ageVerifyNonce.name);

        for (const match of queryParamMatches) {
            if (3 !== match.length) {
                continue;
            }
            params[match[1]] = match[2];
        }

        const result = this.checkVerifiedByParams(params, nonce);

        if (true === result) {
            this.prefersLongSession = !!(params['ag_remember_me'] && 'yes' === params['ag_remember_me']);
        }

        return result;
    }

    isAccessGranted() {
        if (this.options.allowDirectContinue) {
            const directContinueValue = getCookie(this.options.cookies.directContinueState.name);

            if ('yes' === directContinueValue) {
                this.setVerificationCookie(this.options.cookies.directContinueState, 'yes');

                return true;
            }
        }

        if (this.options.requireAgeVerification) {
            const ageVerificationValue = getCookie(this.options.cookies.ageVerifyState.name);

            if ('yes' === ageVerificationValue) {
                this.setVerificationCookie(this.options.cookies.ageVerifyState, 'yes');

                return true;
            }
        }

        return false;
    }

    onDirectContinue() {
        if (!this.options.allowDirectContinue) {
            return;
        }

        this.setVerificationCookie(this.options.cookies.directContinueState, 'yes');
    }

    onAgeVerified() {
        if (!this.options.requireAgeVerification) {
            return;
        }

        this.setVerificationCookie(this.options.cookies.ageVerifyState, 'yes');
    }

    mapCookieAttributes(cookieOptions) {
        const overrideAttr = {};

        if (cookieOptions.expires) {
            if ('year' === cookieOptions.expires) {
                overrideAttr.expires = 365;
            } else if ('month' === cookieOptions.expires) {
                overrideAttr.expires = 30;
            } else if ('week' === cookieOptions.expires) {
                overrideAttr.expires = 7;
            } else if ('session' === cookieOptions.expires) {
                // NOOP
            } else if ('number' === typeof cookieOptions.expires || cookieOptions.expires instanceof Date) {
                overrideAttr.expires = cookieOptions.expires;
            } else {
                throw '[AGEGO] Unexpected expiration value for cookie ' + cookieOptions.name +
                ', expected keyword "year", "month", "week", "session", Date object or integer';
            }
        }

        return merge(cookieOptions.attr, overrideAttr);
    }

    setVerificationCookie(cookieDefinition, value) {
        const cookieAttributes = this.mapCookieAttributes(
            cookieDefinition,
            cookieDefinition.attr
        )

        // Force to use cookie lifetime as set by the user
        if (this.prefersLongSession) {
            cookieAttributes['expires'] = 365;
        }

        setCookie(cookieDefinition.name, value, cookieAttributes);
    }

    checkVerifiedByParams(params, nonce) {
        if (params.ag_nonce !== nonce) {
            return {error: ACCESS_ERROR_CODES.nonceInvalid};
        }

        if (params.ag_error && 'none' !== params.ag_error) {
            return {error: ACCESS_ERROR_CODES.agegoError, ag_error: params.ag_error};
        }

        const expiresAfter = new Date(parseInt(params.ag_expires));

        if (expiresAfter < new Date()) {
            return {error: ACCESS_ERROR_CODES.expired};
        }

        if ('yes' !== params.ag_verified) {
            return {error: ACCESS_ERROR_CODES.notVerified};
        }

        return true;
    }

    getCreateSessionNonce() {
        if (this.options.nonce) {
            return this.options.nonce;
        }

        const cookieNonce = getCookie(this.options.cookies.ageVerifyNonce.name);

        if (cookieNonce) {
            return cookieNonce;
        }

        const nonce = this.generateNonce(16);

        this.setVerificationCookie(this.options.cookies.ageVerifyNonce, nonce);

        return nonce;
    }

    generateNonce(maxLen) {
        let result = '';
        const charactersLength = NONCE_ALPHABET.length;
        for (let i = 0; i < maxLen; i++) {
            result += NONCE_ALPHABET.charAt(Math.floor(Math.random() * charactersLength));
        }
        return result;
    }

    createRedirectURL(baseUrl, nonce) {
        let params = {
            siteid: this.options.siteId,
            returnto: this.options.returnTo || document.location.href,
            nonce: nonce
        };
        if (this.options.callbackToken !== undefined) {
            params.callbacktoken = this.options.callbackToken;
        }
        return baseUrl + '/s2s/start/' + objectToQueryString(params);
    }
}

export {AccessManager, ACCESS_ERROR_CODES};
